import React, { useState, useEffect, useCallback } from 'react';
import { kegsApi, kegRecordsApi } from '../../services/api';
import AdminPageHeader from '../../components/AdminPageHeader';
import './FarmStore.css';

const EMPTY_FORM = {
  keg_id: '',
  flavor: '',
  start_weight: '',
  notes: '',
};

export default function FarmStore() {
  const [tappedKegs, setTappedKegs] = useState([]);
  const [availableKegs, setAvailableKegs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showTapForm, setShowTapForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await kegsApi.getAll();
      const allKegs = data.kegs || [];
      setTappedKegs(allKegs.filter(k => k.location === 'farm_store' && k.status === 'in_use'));
      setAvailableKegs(allKegs.filter(k => k.status === 'available'));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  }

  async function handleTap(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await kegsApi.update({
        id: form.keg_id,
        status: 'in_use',
        location: 'farm_store',
        current_weight: form.start_weight,
        notes: form.notes,
      });
      await kegRecordsApi.create({
        market_day_id: 'farm_store',
        keg_id: form.keg_id,
        flavor: form.flavor,
        start_weight: form.start_weight,
        finish_weight: '',
        weight_used: '',
        notes: form.notes || 'Tapped at farm store',
      });
      await load();
      setShowTapForm(false);
      setForm(EMPTY_FORM);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function updateWeight(keg, newWeight) {
    try {
      await kegsApi.update({ ...keg, current_weight: newWeight });
      setTappedKegs(ks => ks.map(k => k.id === keg.id ? { ...k, current_weight: newWeight } : k));
    } catch (err) {
      setError(err.message);
    } finally {
    }
  }

  async function markKicked(keg) {
    try {
      await kegsApi.update({ ...keg, status: 'kicked', location: 'cc_cooler' });
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div className="page">
      <AdminPageHeader
        title="Farm Store"
        subtitle={`${tappedKegs.length} keg${tappedKegs.length !== 1 ? 's' : ''} currently tapped`}
        action={<button className="btn-primary" onClick={() => setShowTapForm(true)}>+ Tap Keg</button>}
      />
      <div className="page-body">
        {error && <div className="error-banner">{error}</div>}

        {loading ? (
          <div className="loading-state">Loading...</div>
        ) : tappedKegs.length === 0 ? (
          <div className="empty-state">No kegs currently tapped. Tap a keg to get started.</div>
        ) : (
          <div className="markets-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Flavor</th>
                  <th>Current Weight (lbs)</th>
                  <th>Update Weight</th>
                  <th>Notes</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {tappedKegs.map(keg => (
                  <tr key={keg.id}>
                    <td className="td-name">{keg.flavor}</td>
                    <td className="td-price">{keg.current_weight ? `${keg.current_weight} lbs` : '—'}</td>
                    <td>
                      <div className="weight-update">
                        <input
                          type="number"
                          step="0.1"
                          className="qty-input"
                          placeholder="New weight"
                          onBlur={e => e.target.value && updateWeight(keg, e.target.value)}
                        />
                        <span className="weight-hint">lbs</span>
                      </div>
                    </td>
                    <td className="td-cat">{keg.notes || '—'}</td>
                    <td>
                      <button className="btn-kick" onClick={() => markKicked(keg)}>
                        Mark Kicked
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showTapForm && (
        <div className="modal-overlay" onClick={() => setShowTapForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Tap a Keg</h2>
              <button className="modal-close" onClick={() => setShowTapForm(false)}>x</button>
            </div>
            <form onSubmit={handleTap} className="modal-form">
              <div className="field">
                <label>Select Keg *</label>
                <select
                  name="keg_id"
                  value={form.keg_id}
                  onChange={e => {
                    const keg = availableKegs.find(k => String(k.id) === e.target.value);
                    setForm(f => ({ ...f, keg_id: e.target.value, flavor: keg ? keg.flavor : '' }));
                  }}
                  required
                >
                  <option value="">-- Select a keg --</option>
                  {availableKegs.map(k => (
                    <option key={k.id} value={k.id}>{k.flavor}</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Starting Weight (lbs) *</label>
                <input name="start_weight" type="number" step="0.1"
                  value={form.start_weight} onChange={handleChange}
                  placeholder="55.0" required />
              </div>
              <div className="field">
                <label>Notes</label>
                <input name="notes" value={form.notes} onChange={handleChange}
                  placeholder="e.g. Partial keg from Monroe market" />
              </div>
              {error && <div className="form-error">{error}</div>}
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowTapForm(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Tapping...' : 'Tap Keg'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
